import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
  useSuspenseQuery,
  type UseQueryResult,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query'
import { useCallback } from 'react'

import { companyService } from '@/features/company/api/companyService'
import type {
  CompanyDetail,
  CompanyFloorsheetPage,
  CompanyTrade,
} from '@/features/company/api/companyDetail'
import { companyKeys, type CompanyListFilters } from '@/features/company/api/keys'
import type { CompanyListRow } from '@/schemas/company'

export type CompanyListItem = CompanyListRow

/**
 * The `/companies` route's MAIN data — the first page for the URL's filters,
 * awaited by its loader — as key, fetch and paging only; `useCompanyList`
 * below adds the rest.
 */
export const companyListQueryOptions = (filters: CompanyListFilters = {}) =>
  infiniteQueryOptions({
    queryKey: companyKeys.list(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => companyService.list(filters, pageParam),
    getNextPageParam: (last) => last.nextCursor,
  })

/**
 * Paginated company list assembled from the live, activity, search, and
 * heatmap endpoints because v5 does not publish a separate companies path.
 *
 * No `refetchInterval` here on purpose — it used to carry `livePolling(isOpen)`,
 * but a `refetchInterval` on an INFINITE query refetches every loaded page, not
 * just the first: a member who had scrolled to load 10 pages got the whole
 * 10-page fan-out reissued every 20s while the market was open, which is 10x
 * the request volume of every other live list in the app for the same data.
 * That is not a freshness feature, it is a bug. If live freshness on this
 * list is genuinely needed later, the right tool is a targeted refetch of
 * page one only, or a "new listings" affordance the member triggers — neither
 * is built here; this hook just stops doing the wrong thing.
 *
 * `keepPreviousData`: `filters` (search text, sector) is part of the key, so
 * every keystroke, once CompaniesScreen debounces it, would otherwise blank
 * the whole table back to a skeleton while the next result loads.
 */
export const useCompanyList = (filters: CompanyListFilters = {}) =>
  useInfiniteQuery({ ...companyListQueryOptions(filters), placeholderData: keepPreviousData })

/**
 * One company for the Stock page — that route's MAIN data, awaited by its
 * loader. `data === null` is the API's own answer that no active company has
 * this symbol — the screen's "not found" — and is distinct from a failed
 * request, which the loader throws to the route's error component.
 */
export const companyDetailQueryOptions = (symbol: string) =>
  queryOptions<CompanyDetail | null>({
    queryKey: companyKeys.detail(symbol),
    queryFn: () => companyService.detail(symbol),
  })

/**
 * The Stock screen's read of it. Never pending — the loader has cached it —
 * and never an error while it has data: a failed refetch keeps the company
 * on screen, because a suspense query only throws while it has nothing to
 * draw.
 */
export const useSuspenseCompanyDetail = (
  symbol: string,
): UseSuspenseQueryResult<CompanyDetail | null> =>
  useSuspenseQuery(companyDetailQueryOptions(symbol))

/**
 * One server page of a company's trades, keyed and fetched on the symbol
 * alone — so StockScreen can start it from the URL before the company detail
 * (and with it the name) has arrived. The cached rows carry the symbol in
 * `name`; `useCompanyFloorsheet` stamps the real name on through `select`.
 */
export const companyFloorsheetQueryOptions = (symbol: string, page: number, pageSize: number) =>
  queryOptions<CompanyFloorsheetPage>({
    queryKey: companyKeys.floorsheet(symbol, page, pageSize),
    queryFn: () => companyService.floorsheet(symbol, page, pageSize, symbol),
  })

/**
 * `name` rides along for the table's symbol cell and is not part of the key —
 * it is the same company either way. It is applied per reader in `select`,
 * never baked into the cached page, which is what lets the page be fetched
 * before the name is known.
 */
export const useCompanyFloorsheet = (
  symbol: string,
  name: string,
  page: number,
  pageSize: number,
): UseQueryResult<CompanyFloorsheetPage> => {
  const withName = useCallback(
    (data: CompanyFloorsheetPage): CompanyFloorsheetPage => ({
      ...data,
      rows: data.rows.map((row) => ({ ...row, name })),
    }),
    [name],
  )

  return useQuery({
    ...companyFloorsheetQueryOptions(symbol, page, pageSize),
    enabled: symbol.length > 0,
    placeholderData: keepPreviousData,
    select: withName,
  })
}

/**
 * The biggest prints of one company's latest floorsheet session, by amount.
 * Its own key rather than a slice of `companyFloorsheetQueryOptions`: that page
 * is newest-first, and the largest print of the day is rarely on its first page.
 */
export const companyLargestTradesQueryOptions = (symbol: string, count: number) =>
  queryOptions<CompanyTrade[]>({
    queryKey: companyKeys.largestTrades(symbol, count),
    queryFn: ({ client }) => companyService.largestTrades(symbol, count, client),
  })

export const useCompanyLargestTrades = (
  symbol: string,
  count: number,
): UseQueryResult<CompanyTrade[]> =>
  useQuery({ ...companyLargestTradesQueryOptions(symbol, count), enabled: symbol.length > 0 })
